import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { emailTemplateAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import './EmailTemplates.css';

/**
 * Merge-tag reference rows from API include pattern rows (e.g. ProNEmail). Expand N=1…9 for the insert dropdown.
 */
function expandMergeTagRowsForInsert(rows) {
  const out = [];
  for (const row of rows || []) {
    const { tag, description } = row;
    const desc = description || '';
    if (tag === 'ProNFirst, ProNLast') {
      for (let n = 1; n <= 9; n += 1) {
        out.push({ tag: `Pro${n}First`, description: `Provider team member ${n} first name (client provider team)` });
        out.push({ tag: `Pro${n}Last`, description: `Provider team member ${n} last name (client provider team)` });
      }
      continue;
    }
    if (desc.includes('Replace N with 1 through 9')) {
      const proSlot = tag.match(/^ProN(.+)$/);
      if (proSlot) {
        const suffix = proSlot[1];
        for (let n = 1; n <= 9; n += 1) {
          out.push({ tag: `Pro${n}${suffix}`, description });
        }
        continue;
      }
      if (tag.endsWith('N') && tag.length > 1) {
        const base = tag.slice(0, -1);
        for (let n = 1; n <= 9; n += 1) {
          out.push({ tag: `${base}${n}`, description });
        }
        continue;
      }
    }
    out.push({ tag, description });
  }
  out.sort((a, b) => a.tag.localeCompare(b.tag, 'en', { sensitivity: 'base', numeric: true }));
  return out;
}

function EmailTemplates() {
  const canManage = hasPermission('email_templates.manage');
  const [templates, setTemplates] = useState([]);
  const skipNextSaveRef = useRef(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    template_code: '',
    display_name: '',
    description: '',
    category: '',
    language: 'EN',
    subject_template: '',
    body_start_template: '',
    body_end_template: '',
    to_default: '',
    cc_default: '',
    enabled: true,
    follow_up_todo_enabled: false,
    follow_up_todo_delay_days: '10',
    follow_up_todo_title: '',
    follow_up_todo_description: '',
    follow_up_todo_visible_from_date_offset: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [nameFilter, setNameFilter] = useState(''); // search under Display Name
  const [languageFilter, setLanguageFilter] = useState(''); // '' = all, 'EN' = English, 'ES' = Spanish
  const [categoryFilter, setCategoryFilter] = useState(''); // '' = all, or category string
  const [bodyModalField, setBodyModalField] = useState(null); // 'body_start_template' | 'body_end_template' | null
  const [bodyModalValue, setBodyModalValue] = useState('');
  const [insertFieldSelect, setInsertFieldSelect] = useState(''); // for "Insert merge field" dropdown (reset after insert)
  const bodyModalTextareaRef = useRef(null);
  const bodyModalInsertPositionRef = useRef(null); // cursor position to restore after inserting a tag
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const showMergeRefView = location.pathname.includes('merge-fields');
  const [mergeTagRows, setMergeTagRows] = useState([]);
  const [mergeTagLoading, setMergeTagLoading] = useState(true);
  const [mergeTagError, setMergeTagError] = useState(null);

  const mergeInsertOptions = useMemo(() => expandMergeTagRowsForInsert(mergeTagRows), [mergeTagRows]);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setMergeTagLoading(true);
    setMergeTagError(null);
    emailTemplateAPI
      .getMergeTagReference()
      .then((res) => {
        if (!cancelled) setMergeTagRows(res.data?.tags || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setMergeTagRows([]);
          setMergeTagError(err.response?.data?.error || err.message || 'Failed to load merge fields');
        }
      })
      .finally(() => {
        if (!cancelled) setMergeTagLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await emailTemplateAPI.getAll();
      setTemplates(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load email templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    if (template.id) {
      setEditingId(template.id);
      setIsCreating(false);
      skipNextSaveRef.current = true;
    } else {
      setEditingId(null);
      setIsCreating(true);
    }
    const cfg = template.follow_up_todo || null;
    setFormData({
      template_code: template.template_code || '',
      display_name: template.display_name || '',
      description: template.description || '',
      category: template.category || '',
      language: template.language || 'EN',
      subject_template: template.subject_template || '',
      body_start_template: template.body_start_template || '',
      body_end_template: template.body_end_template || '',
      to_default: template.to_default || '',
      cc_default: template.cc_default || '',
      enabled: template.enabled !== false,
      follow_up_todo_enabled: !!cfg,
      follow_up_todo_delay_days: cfg?.delay_days != null ? String(cfg.delay_days) : '10',
      follow_up_todo_title: cfg?.title || '',
      follow_up_todo_description: cfg?.description || '',
      follow_up_todo_visible_from_date_offset: cfg?.visible_from_date_offset != null ? String(cfg.visible_from_date_offset) : '',
    });
    setSaveMessage(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    setBodyModalField(null);
    setFormData({
      template_code: '',
      display_name: '',
      description: '',
      category: '',
      language: 'EN',
      subject_template: '',
      body_start_template: '',
      body_end_template: '',
      to_default: '',
      cc_default: '',
      enabled: true,
      follow_up_todo_enabled: false,
      follow_up_todo_delay_days: '10',
      follow_up_todo_title: '',
      follow_up_todo_description: '',
      follow_up_todo_visible_from_date_offset: '',
    });
    setSaveMessage(null);
    loadTemplates(); // Refresh list when leaving edit so list is up to date
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buildApiPayload = (fd) => {
    const {
      follow_up_todo_enabled,
      follow_up_todo_delay_days,
      follow_up_todo_title,
      follow_up_todo_description,
      follow_up_todo_visible_from_date_offset,
      ...rest
    } = fd;
    const offsetVal = follow_up_todo_visible_from_date_offset !== '' ? parseInt(follow_up_todo_visible_from_date_offset, 10) : null;
    const follow_up_todo = follow_up_todo_enabled
      ? {
          delay_days: parseInt(follow_up_todo_delay_days, 10) || 10,
          title: (follow_up_todo_title || '').trim() || 'Follow up on sent email',
          description: (follow_up_todo_description || '').trim() || null,
          visible_from_date_offset: !Number.isNaN(offsetVal) ? offsetVal : null,
        }
      : null;
    return { ...rest, follow_up_todo };
  };

  /** Insert a merge-field tag at the current cursor position in the body modal textarea. */
  const insertMergeFieldInBodyModal = (tag) => {
    const ta = bodyModalTextareaRef.current;
    const start = ta ? ta.selectionStart : bodyModalValue.length;
    const end = ta ? ta.selectionEnd : bodyModalValue.length;
    const snippet = `{{ ${tag} }}`;
    const newValue = bodyModalValue.slice(0, start) + snippet + bodyModalValue.slice(end);
    bodyModalInsertPositionRef.current = start + snippet.length;
    setBodyModalValue(newValue);
  };

  useEffect(() => {
    if (bodyModalInsertPositionRef.current == null || !bodyModalTextareaRef.current) return;
    const pos = bodyModalInsertPositionRef.current;
    bodyModalInsertPositionRef.current = null;
    bodyModalTextareaRef.current.setSelectionRange(pos, pos);
    bodyModalTextareaRef.current.focus();
  }, [bodyModalValue]);

  // Auto-save when editing (debounced). Do not refetch list after save to avoid flicker.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  useEffect(() => {
    if (!editingId) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    const t = setTimeout(async () => {
      const data = buildApiPayload(formDataRef.current);
      setSaving(true);
      setSaveMessage(null);
      try {
        await emailTemplateAPI.update(editingId, data);
        setSaveMessage('Saved');
        setTimeout(() => setSaveMessage(null), 2000);
      } catch (err) {
        setSaveMessage(err.response?.data?.error || err.message || 'Failed to save');
        setTimeout(() => setSaveMessage(null), 3000);
      } finally {
        setSaving(false);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [formData, editingId]);

  const handleSave = async () => {
    if (!isCreating) return;
    try {
      setSaving(true);
      setSaveMessage(null);
      await emailTemplateAPI.create(buildApiPayload(formData));
      await loadTemplates();
      handleCancel();
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const escapeCsvCell = (val) => {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleMergeRefDownload = () => {
    const header = 'Tag,Description,Source';
    const rows = mergeTagRows.map((r) => [r.tag, r.description, r.source].map(escapeCsvCell).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email-merge-field-reference.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const escapeHtml = (s) => {
    const div = document.createElement('div');
    div.textContent = s ?? '';
    return div.innerHTML;
  };

  const handleMergeRefPrint = () => {
    const title = 'Email Merge Fields';
    const intro =
      'This list matches Document Generation merge fields (same tag names, descriptions, and sources). In email subject, body, default To, and default CC, wrap each tag in double curly braces — for example {{CL_FIRST}} or {{PRO_TEAM_EMAIL}}. Client provider team (Edit Client → Provider team) slots use Pro1Email … Pro9Email, or use PRO_TEAM_EMAIL for all provider team emails that have a value (comma-separated).';
    const rows = mergeTagRows.map((r) => `<tr><td><code>${escapeHtml(`{{ ${r.tag} }}`)}</code></td><td>${escapeHtml(r.description)}</td><td>${escapeHtml(r.source)}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
      body{font-family:system-ui,sans-serif;padding:1.5rem;font-size:0.9rem;}
      h1{margin:0 0 0.5rem;font-size:1.25rem;}
      p{color:#495057;margin:0 0 1rem;}
      table{border-collapse:collapse;width:100%;}
      th,td{padding:0.4rem 0.6rem;text-align:left;border-bottom:1px solid #eee;}
      th{background:#f8f9fa;font-weight:600;}
      code{background:#f1f3f5;padding:0.15rem 0.35rem;border-radius:4px;font-size:0.85em;}
    </style></head><body><h1>${escapeHtml(title)}</h1><p>${escapeHtml(intro)}</p><table><thead><tr><th>Tag</th><th>Description</th><th>Source</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 250);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email template?')) {
      return;
    }

    try {
      await emailTemplateAPI.delete(id);
      await loadTemplates();
      if (editingId === id) {
        handleCancel();
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to delete template');
    }
  };

  if (loading) {
    return <div className="email-templates-page"><p>Loading email templates...</p></div>;
  }

  if (error) {
    return <div className="email-templates-page"><p className="error-msg">{error}</p></div>;
  }

  if (editingId || isCreating) {
    return (
      <div className="email-templates-edit-root">
        <div className="email-templates-page">
          <ContentHeader
          title={editingId ? (formData.display_name || 'Edit Template') : 'Create Template'}
          titleHint={
            editingId
              ? 'Changes to this template save automatically after a short pause. Use Back to template list or Close when you are done—your saved work is kept.'
              : 'Use Back to template list or Close to return without creating. Click Create to save the new template.'
          }
          onBack={handleCancel}
          backLabel="Back to template list"
          actions={
            <div className="email-templates-edit-header-actions">
              {editingId && saveMessage && (
                <span className={saveMessage.startsWith('Failed') ? 'email-templates-status error' : 'email-templates-status'}>
                  {saveMessage}
                </span>
              )}
              <button type="button" onClick={handleCancel} className="btn-cancel">
                Close
              </button>
            </div>
          }
        />

        <div className="email-template-form-section">
          <div className="email-template-form">
            <div className="form-row">
              <label>Template Code *</label>
              <input
                type="text"
                value={formData.template_code}
                onChange={(e) => handleChange('template_code', e.target.value)}
                placeholder="e.g. team-providers-payees"
                disabled={!!editingId}
                className="form-input form-input-wide"
              />
            </div>

            <div className="form-row">
              <label>Display Name *</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => handleChange('display_name', e.target.value)}
                placeholder="e.g. Team Update (Providers and Payees)"
                className="form-input form-input-wide"
              />
            </div>

            <div className="form-row">
              <label>Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description"
                className="form-input form-input-wide"
              />
            </div>

            <div className="form-row">
              <label>Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="e.g. Team Communication"
                className="form-input form-input-wide"
              />
            </div>

            <div className="form-row">
              <label>Language</label>
              <select
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="form-input"
                style={{ maxWidth: '300px' }}
              >
                <option value="EN">English</option>
                <option value="ES">Spanish</option>
              </select>
            </div>

            <div className="form-row">
              <label>Subject Template *</label>
              <input
                type="text"
                value={formData.subject_template}
                onChange={(e) => handleChange('subject_template', e.target.value)}
                placeholder="e.g. Team Update - {{client.firstName}} {{client.lastName}}"
                className="form-input form-input-wide"
              />
              <small className="field-hint">Use placeholders like {'{{client.firstName}}'}, {'{{client.lastName}}'}</small>
            </div>

            <div className="form-row">
              <label className="form-row-label-with-action">
                <span>Body Start (Preamble)</span>
                <button
                  type="button"
                  className="btn-expand-body"
                  onClick={() => {
                    setBodyModalValue(formData.body_start_template || '');
                    setBodyModalField('body_start_template');
                  }}
                >
                  Expand
                </button>
              </label>
              <textarea
                value={formData.body_start_template}
                onChange={(e) => handleChange('body_start_template', e.target.value)}
                onDoubleClick={() => {
                  setBodyModalValue(formData.body_start_template || '');
                  setBodyModalField('body_start_template');
                }}
                placeholder="Dear Team,&#10;&#10;Use {{CL_FIRST}}, {{SC_PHONE}}, etc. for merge fields..."
                rows="4"
                className="form-textarea"
              />
              <small className="field-hint">Text that appears at the beginning of the email body. Double-click or Expand to edit in a larger window.</small>
            </div>

            <div className="form-row">
              <label className="form-row-label-with-action">
                <span>Body End (Suffix)</span>
                <button
                  type="button"
                  className="btn-expand-body"
                  onClick={() => {
                    setBodyModalValue(formData.body_end_template || '');
                    setBodyModalField('body_end_template');
                  }}
                >
                  Expand
                </button>
              </label>
              <textarea
                value={formData.body_end_template}
                onChange={(e) => handleChange('body_end_template', e.target.value)}
                onDoubleClick={() => {
                  setBodyModalValue(formData.body_end_template || '');
                  setBodyModalField('body_end_template');
                }}
                placeholder="Thank you!"
                rows="3"
                className="form-textarea"
              />
              <small className="field-hint">Text that appears at the end of the email body. Double-click or Expand to edit in a larger window.</small>
            </div>

            <div className="form-row">
              <label>Default To Recipients</label>
              <input
                type="text"
                value={formData.to_default}
                onChange={(e) => handleChange('to_default', e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                className="form-input form-input-wide"
              />
              <small className="field-hint">
                Comma or semicolon-separated. On <strong>Client Email</strong>, merge tags are filled (e.g.{' '}
                <code>{'{{ SC_EMAIL }}'}</code> for the assigned service coordinator). Primary contact is also added to To when present.
              </small>
            </div>

            <div className="form-row">
              <label>Default CC Recipients</label>
              <input
                type="text"
                value={formData.cc_default}
                onChange={(e) => handleChange('cc_default', e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                className="form-input form-input-wide"
              />
              <small className="field-hint">
                Same as default To: comma or semicolon-separated; merge tags supported on Client Email (e.g.{' '}
                <code>{'{{ SC_EMAIL }}'}</code>).
              </small>
            </div>

            <div className="form-row">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
                Enabled
              </label>
            </div>

            <div className="email-template-form-section">
              <h4 className="email-template-section-title">Follow-up To-Do</h4>
              <div className="form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.follow_up_todo_enabled}
                    onChange={(e) => handleChange('follow_up_todo_enabled', e.target.checked)}
                  />
                  Create a follow-up to-do when this email is sent
                </label>
              </div>
              {formData.follow_up_todo_enabled && (
                <>
                  <div className="form-row">
                    <label>Delay (days) *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.follow_up_todo_delay_days}
                      onChange={(e) => handleChange('follow_up_todo_delay_days', e.target.value)}
                      className="form-input"
                      style={{ width: '80px' }}
                    />
                    <small className="field-hint">Days after send to set the to-do's due date.</small>
                  </div>
                  <div className="form-row">
                    <label>To-do title *</label>
                    <input
                      type="text"
                      value={formData.follow_up_todo_title}
                      onChange={(e) => handleChange('follow_up_todo_title', e.target.value)}
                      placeholder="Check for reply on no-contact letter"
                      className="form-input form-input-wide"
                    />
                  </div>
                  <div className="form-row">
                    <label>To-do description</label>
                    <input
                      type="text"
                      value={formData.follow_up_todo_description}
                      onChange={(e) => handleChange('follow_up_todo_description', e.target.value)}
                      placeholder="Optional additional context for the to-do"
                      className="form-input form-input-wide"
                    />
                  </div>
                  <div className="form-row">
                    <label>Hide for first N days</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.follow_up_todo_visible_from_date_offset}
                      onChange={(e) => handleChange('follow_up_todo_visible_from_date_offset', e.target.value)}
                      placeholder="e.g. 9"
                      className="form-input"
                      style={{ width: '80px' }}
                    />
                    <small className="field-hint">To-do stays hidden until this many days after send. Leave blank to show immediately.</small>
                  </div>
                </>
              )}
            </div>

            <div className="form-actions">
              {isCreating ? (
                <>
                  <button type="button" onClick={handleCancel} className="btn-cancel">Close</button>
                  <button type="button" onClick={handleSave} disabled={saving || !formData.template_code?.trim() || !formData.display_name?.trim()} className="btn-save">
                    {saving ? 'Creating…' : 'Create'}
                  </button>
                  {saveMessage && !saveMessage.includes('successfully') && (
                    <span className="save-message save-message-err">{saveMessage}</span>
                  )}
                </>
              ) : (
                <>
                  <button type="button" onClick={handleCancel} className="btn-cancel">Close</button>
                  {saveMessage && <span className={saveMessage.startsWith('Failed') ? 'save-message save-message-err' : 'save-message save-message-ok'}>{saveMessage}</span>}
                </>
              )}
            </div>
          </div>
        </div>
        </div>

        {bodyModalField && (
          <div className="email-template-body-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="email-template-body-modal-title">
            <div className="email-template-body-modal">
              <div className="email-template-body-modal-header">
                <h3 id="email-template-body-modal-title">{bodyModalField === 'body_start_template' ? 'Body Start (Preamble)' : 'Body End (Suffix)'}</h3>
                <button type="button" className="email-template-body-modal-close" onClick={() => setBodyModalField(null)} aria-label="Close">×</button>
              </div>
              <textarea
                ref={bodyModalTextareaRef}
                className="email-template-body-modal-textarea"
                value={bodyModalValue}
                onChange={(e) => setBodyModalValue(e.target.value)}
                placeholder={bodyModalField === 'body_start_template' ? 'Use {{CL_FIRST}}, {{SC_PHONE}}, etc.' : 'Thank you!'}
                spellCheck="true"
              />
              <div className="email-template-body-modal-insert-row">
                <label htmlFor="email-template-body-modal-insert-field">Insert merge field</label>
                <select
                  id="email-template-body-modal-insert-field"
                  className="email-template-body-modal-insert-select"
                  value={insertFieldSelect}
                  onChange={(e) => {
                    const tag = e.target.value;
                    if (tag) {
                      insertMergeFieldInBodyModal(tag);
                      setInsertFieldSelect('');
                    }
                  }}
                  aria-label="Insert merge field at cursor"
                >
                  <option value="">Select a field…</option>
                  {mergeTagLoading && <option value="" disabled>Loading merge fields…</option>}
                  {!mergeTagLoading &&
                    mergeInsertOptions.map(({ tag, description }) => (
                      <option key={tag} value={tag}>{`{{ ${tag} }} — ${description}`}</option>
                    ))}
                </select>
              </div>
              <div className="email-template-body-modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setBodyModalField(null)}>Close</button>
                <button type="button" className="btn-save" onClick={() => { handleChange(bodyModalField, bodyModalValue); setBodyModalField(null); }}>Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (showMergeRefView) {
    return (
      <div className="email-templates-page">
        <ContentHeader
          title="Email Merge Fields"
          backTo={tenantPath('/settings/email-templates')}
          backLabel="Back to Email Templates"
          actions={
            <div className="email-templates-header-actions">
              <button type="button" className="email-templates-merge-ref-print-btn" onClick={handleMergeRefPrint}>
                Print
              </button>
              <button type="button" className="email-templates-merge-ref-download-btn" onClick={handleMergeRefDownload}>
                Download
              </button>
            </div>
          }
        />
        <p className="email-templates-merge-ref-intro email-templates-merge-ref-intro-page">
          This list is the same catalog as <strong>Settings → Document Templates → Doc Gen Fields</strong> (tag names, descriptions, sources). In email templates, wrap each tag in <strong>double curly braces</strong> in the subject, body, default To, and default CC — for example <code>{'{{CL_FIRST}}'}</code>, <code>{'{{Pro3Email}}'}</code>, or <code>{'{{PRO_TEAM_EMAIL}}'}</code> for all client provider team emails that have a value (comma-separated). Rows that show <code>ProN…</code> refer to the client provider team (Edit Client → Provider team); replace N with 1 through 9.
        </p>
        {mergeTagError && <p className="error-msg" role="alert">{mergeTagError}</p>}
        {mergeTagLoading && <p>Loading merge field list…</p>}
        {!mergeTagLoading && !mergeTagError && (
        <div className="email-templates-merge-ref-table-wrap email-templates-merge-ref-table-wrap-page">
          <table className="email-templates-merge-ref-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Description</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {mergeTagRows.map((row) => (
                <tr key={row.tag}>
                  <td className="email-templates-merge-ref-tag"><code>{`{{ ${row.tag} }}`}</code></td>
                  <td>{row.description}</td>
                  <td>{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="email-templates-page">
      <ContentHeader
        title="Email Templates"
        actions={
          <div className="email-templates-header-actions">
            <button type="button" className="email-templates-merge-ref-btn" onClick={() => navigate(tenantPath('/settings/email-templates/merge-fields'))}>
              Email merge fields
            </button>
            {canManage && (
              <button onClick={() => handleEdit({})} className="btn-add">+ Create Template</button>
            )}
          </div>
        }
      />

      <div className="email-templates-list">
        {templates.length === 0 ? (
          <p className="no-templates">
            {canManage ? 'No email templates found. Click "Create Template" to add one.' : 'No email templates found.'}
          </p>
        ) : (() => {
          const categories = [...new Set(templates.map(t => t.category).filter(Boolean))].sort();
          const filtered = templates.filter(t => {
            if (nameFilter.trim()) {
              const q = nameFilter.trim().toLowerCase();
              const name = (t.display_name || t.template_code || '').toLowerCase();
              const code = (t.template_code || '').toLowerCase();
              if (!name.includes(q) && !code.includes(q)) return false;
            }
            if (languageFilter && t.language !== languageFilter) return false;
            if (categoryFilter && (t.category || '') !== categoryFilter) return false;
            return true;
          });
          if (filtered.length === 0) {
            return (
              <p className="no-templates">
                No templates match the selected filters.
              </p>
            );
          }
          return (
            <div className="email-templates-table-wrap">
            <table className="email-templates-table">
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th className="email-templates-th-lang" title="Language">Lang</th>
                  <th>Category</th>
                  {canManage && <th className="email-templates-th-actions">Actions</th>}
                </tr>
                <tr className="email-templates-filter-row">
                  <th>
                    <input
                      type="text"
                      className="email-templates-filter-input"
                      placeholder="Search name..."
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      aria-label="Filter by display name"
                    />
                  </th>
                  <th>
                    <select
                      className="email-templates-filter-select"
                      value={languageFilter}
                      onChange={(e) => setLanguageFilter(e.target.value)}
                      aria-label="Filter by language"
                    >
                      <option value="">All</option>
                      <option value="EN">E</option>
                      <option value="ES">S</option>
                    </select>
                  </th>
                  <th>
                    <select
                      className="email-templates-filter-select"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      aria-label="Filter by category"
                    >
                      <option value="">All</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {filtered.map(template => (
                  <tr
                    key={template.id}
                    className={`email-templates-tr ${canManage ? 'clickable-row' : ''}`}
                    onClick={canManage ? () => handleEdit(template) : undefined}
                    role={canManage ? 'button' : undefined}
                    tabIndex={canManage ? 0 : undefined}
                    onKeyDown={canManage ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(template); } } : undefined}
                  >
                    <td>
                      <span className="template-row-name">
                        {template.display_name || template.template_code}
                        {!template.enabled && <span className="badge badge-disabled">Disabled</span>}
                      </span>
                    </td>
                    <td className="email-templates-td-lang" title={template.language === 'EN' ? 'English' : template.language === 'ES' ? 'Spanish' : template.language || ''}>
                      {template.language === 'EN' ? 'E' : template.language === 'ES' ? 'S' : (template.language || '—')}
                    </td>
                    <td>{template.category || '—'}</td>
                    {canManage && (
                      <td className="email-templates-td-actions" onClick={(e) => e.stopPropagation()}>
                        <DeleteIconButton className="btn-delete" onClick={() => handleDelete(template.id)} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default EmailTemplates;